import { AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { ComponentBase } from 'app/core/componentBase';
import { DEFAULT_PAGE_LIMIT, INDEPENDENT_CREDITS_ROUTE, MERCHANTS_ROUTE } from 'app/core/constants';
import { CustomColumn } from 'app/core/models/custom-column';
import { DateFormatPipe } from 'app/core/pipes/date-format.pipe';
import { IndependentCreditStatusPipe } from 'app/core/pipes/independent-credit-status.pipe';
import { AuthService } from 'app/core/services/auth.service';
import { IndependentCreditsAppService } from 'app/core/services/independent-credits.app.service';
import { PricingTemplateAppService } from 'app/core/services/pricing-template.app.service';
import { CustomColumnComponent } from 'app/shared/custom-column/custom-column.component';
import { Observable, of } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import {
  CardDetails,
  IndependentCredit,
  ListIndependentCreditsRequestParams,
  PaymentMethod,
} from '../../../../projects/tilled-api-client/src';
import { ActionListItem } from '../action-list/action-list.model';
import { TilledChipConfig } from '../tilled-chip/tilled-chip.component';
import { Column } from '../tilled-table/decorators/column';
import { TilledTableComponent } from '../tilled-table/tilled-table.component';

export const CURRENT_INDEPENDENT_CREDITS_CUSTOM_COLUMN_KEY = 'tilled-independent-credits-custom-column';

@Component({
  selector: 'app-independent-credits-list',
  templateUrl: './independent-credits-list.component.html',
  styleUrls: ['./independent-credits-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [TilledTableComponent, AsyncPipe, MatIcon, MatMenuModule, CustomColumnComponent],
})
export class IndependentCreditsListComponent extends ComponentBase implements OnInit, OnChanges {
  @Input() accountId: string = null;
  @Input() isMerchant: boolean;
  @Input() initialQuery: string = null;
  @Input() query: string = null;
  @Input() queryMerchantId: string = null;
  @Input() initialStartDate: string = null;
  @Input() initialEndDate: string = null;
  @Input() startDate: string = null;
  @Input() endDate: string = null;
  @Input() status: IndependentCredit.StatusEnum = null;
  @Input() pageIndex: number = 0;
  @Input() pageSize: number = DEFAULT_PAGE_LIMIT;
  @Input() updateQueryCallback: (page?: number, size?: number) => void;

  public secondaryReasonText = 'Send an outbound transfer to see outbound transfer data.';
  public dialogRef: any;
  public updatedIndependentCredit$: Observable<IndependentCredit>;
  public independentCreditsViewModel$: Observable<IndependentCreditViewModel[]>;
  public independentCreditsCount$: Observable<number>;
  private independentCredits$: Observable<IndependentCredit[]>;

  public selectedActionList: ActionListItem[];
  public hideColumnKeys: IndependentCreditsListViewModel[] = ['id'];
  public sortColumnKeys: IndependentCreditsListViewModel[] = [];
  public isLoading = true;
  public sortInfo = null;

  public customColumns: CustomColumn[] = [];
  updateTableCallback = function (columns: CustomColumn[]): void {
    this.customColumns = columns;
    this.updateTable();
  }.bind(this);

  constructor(
    private _independentCreditsService: IndependentCreditsAppService,
    private _independentCreditStatusPipe: IndependentCreditStatusPipe,
    private _pricingTemplateAppService: PricingTemplateAppService,
    private _dateFormatPipe: DateFormatPipe,
    private _router: Router,
    private _matDialog: MatDialog,
    private _authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.independentCredits$ = this._independentCreditsService.independentCredits$;
    this.independentCredits$
      .pipe(
        takeUntil(this._unsubscribeAll),
        tap((result) => {
          this.independentCreditsViewModel$ = of(this.getViewModelsFromIndependentCredits(result));
          if (this.updateQueryCallback) {
            this.updateQueryCallback(this.pageIndex || 0, this.pageSize || DEFAULT_PAGE_LIMIT);
          }
        }),
      )
      .subscribe();

    this.getIndependentCredits(this.pageSize, this.pageIndex);
    if (this.isMerchant) {
      this.hideColumnKeys = ['merchant_name', 'currency'];
    }

    this.independentCreditsCount$ = this._independentCreditsService.independentCreditsCount$;

    this.updatedIndependentCredit$ = this._independentCreditsService.independentCredit$;
    this.updatedIndependentCredit$.pipe(takeUntil(this._unsubscribeAll)).subscribe((updatedCredit) => {
      this.independentCreditsViewModel$ = this.independentCreditsViewModel$.pipe(
        map((viewModels) =>
          viewModels.map((viewModel) => {
            if (viewModel.id !== updatedCredit.id) {
              return viewModel;
            } else {
              return this.convertIndependentCreditToViewModel(updatedCredit, this.isMerchant);
            }
          }),
        ),
      );
    });

    if (!this.isMerchant) {
      this._pricingTemplateAppService.getPricingTemplates(this.accountId).subscribe((pricingTemplates) => {
        if (!pricingTemplates.find((pricingTemplate) => pricingTemplate.currency === 'cad')) {
          this.hideColumnKeys.push('currency');
        }
      });
    }
  }

  ngAfterViewInit() {
    this.buildInputColumns();
    this.cdr.detectChanges();
  }

  // onChange fires before onInit so we want to load the outbound transfers just once here on
  // page load.
  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.query && !changes.query.firstChange) ||
      (changes.queryMerchantId && !changes.queryMerchantId.firstChange) ||
      (changes.startDate && !changes.startDate.firstChange) ||
      (changes.endDate && !changes.endDate.firstChange) ||
      (changes.accountId && !changes.accountId.firstChange)
    ) {
      this.getIndependentCredits(this.pageSize, 0);
    }
  }

  getIndependentCredits = (size: number, index: number): void => {
    this.pageSize = size;
    this.pageIndex = index;
    const params: ListIndependentCreditsRequestParams = {
      tilledAccount: this.queryMerchantId ?? this.accountId,
      createdAtGte: this.startDate ? new Date(this.startDate).toString() : null,
      createdAtLte: this.endDate ? new Date(this.endDate).toString() : null,
      status: this.status as any,
      includeConnectedAccounts: !this.isMerchant,
      offset: size * index,
      limit: size,
    };

    this._independentCreditsService.getAllIndependentCredits(params);

    if (this.query || this.queryMerchantId || this.startDate || this.endDate) {
      this.secondaryReasonText = 'Update the filters to see results.';
    } else {
      this.secondaryReasonText = 'Create an independent credit to see data.';
    }
  };

  convertIndependentCreditToViewModel(
    independentCredit: IndependentCredit,
    isMerchant: boolean,
  ): IndependentCreditViewModel {
    const temp: IndependentCreditViewModel = new IndependentCreditViewModel();
    temp.id = independentCredit.id;
    temp.independent_credit_id = independentCredit.id;
    temp.merchant_id = independentCredit.account_id;
    temp.merchant_name = independentCredit.account.name;
    temp.created_at = this._dateFormatPipe.transform(independentCredit.created_at);
    temp.merchant_id = independentCredit.account_id;
    if (independentCredit.payment_method?.ach_debit) {
      temp.last_4 = '**' + independentCredit.payment_method.ach_debit.last2;
    } else if (independentCredit.payment_method?.card) {
      temp.last_4 = independentCredit.payment_method.card.last4;
    } else if (independentCredit.payment_method?.card_present) {
      temp.last_4 = independentCredit.payment_method.card_present.last4;
    }
    temp.external_payment_id = independentCredit.source_transaction_id;
    temp.currency = new UpperCasePipe().transform(independentCredit.currency);
    temp.amount = independentCredit.amount;
    temp.status = independentCredit.status;
    temp.status_chip_config = this._independentCreditStatusPipe.transform(independentCredit.status, true);
    temp.action = [];

    temp.action = temp.action.concat([
      {
        name: 'View standalone refund details',
        callback: (): void => {
          this._router.navigate([
            `/${independentCredit.account_id}/${INDEPENDENT_CREDITS_ROUTE}/${independentCredit.id}`,
          ]);
        },
        disabled: false,
      },
    ]);
    if (!isMerchant) {
      temp.action = temp.action.concat([
        {
          name: 'View merchant',
          callback: (): void => {
            this._router.navigate([`/${MERCHANTS_ROUTE}/${independentCredit.account_id}`]);
          },
          disabled: false,
        },
      ]);
    }

    switch (independentCredit.payment_method?.type) {
      case PaymentMethod.TypeEnum.CARD:
        switch (independentCredit.payment_method?.card?.brand) {
          case CardDetails.BrandEnum.AMEX:
            temp.transaction_type = 'assets/images/payment-methods/amex.svg';
            break;
          case CardDetails.BrandEnum.DINERS:
            temp.transaction_type = 'assets/images/payment-methods/diners.svg';
            break;
          case CardDetails.BrandEnum.DISCOVER:
            temp.transaction_type = 'assets/images/payment-methods/discover.svg';
            break;
          case CardDetails.BrandEnum.JCB:
            temp.transaction_type = 'assets/images/payment-methods/jcb.svg';
            break;
          case CardDetails.BrandEnum.MAESTRO:
            temp.transaction_type = 'assets/images/payment-methods/maestro.svg';
            break;
          case CardDetails.BrandEnum.MASTERCARD:
            temp.transaction_type = 'assets/images/payment-methods/mastercard.svg';
            break;
          case CardDetails.BrandEnum.SOLO:
            temp.transaction_type = 'assets/images/payment-methods/solo.svg';
            break;
          case CardDetails.BrandEnum.VISA || CardDetails.BrandEnum.VISA_DEBIT || CardDetails.BrandEnum.VISA_ELECTRON:
            temp.transaction_type = 'assets/images/payment-methods/visa.svg';
            break;
          default:
            temp.transaction_type = 'assets/images/payment-methods/default-card.svg';
        }
        break;
      case PaymentMethod.TypeEnum.CARD_PRESENT:
        switch (independentCredit.payment_method?.card_present?.brand) {
          case CardDetails.BrandEnum.AMEX:
            temp.transaction_type = 'assets/images/payment-methods/amex.svg';
            break;
          case CardDetails.BrandEnum.DINERS:
            temp.transaction_type = 'assets/images/payment-methods/diners.svg';
            break;
          case CardDetails.BrandEnum.DISCOVER:
            temp.transaction_type = 'assets/images/payment-methods/discover.svg';
            break;
          case CardDetails.BrandEnum.JCB:
            temp.transaction_type = 'assets/images/payment-methods/jcb.svg';
            break;
          case CardDetails.BrandEnum.MAESTRO:
            temp.transaction_type = 'assets/images/payment-methods/maestro.svg';
            break;
          case CardDetails.BrandEnum.MASTERCARD:
            temp.transaction_type = 'assets/images/payment-methods/mastercard.svg';
            break;
          case CardDetails.BrandEnum.SOLO:
            temp.transaction_type = 'assets/images/payment-methods/solo.svg';
            break;
          case CardDetails.BrandEnum.VISA || CardDetails.BrandEnum.VISA_DEBIT || CardDetails.BrandEnum.VISA_ELECTRON:
            temp.transaction_type = 'assets/images/payment-methods/visa.svg';
            break;
          default:
            temp.transaction_type = 'assets/images/payment-methods/default-card.svg';
        }
        break;
      case PaymentMethod.TypeEnum.ACH_DEBIT:
        temp.transaction_type = 'assets/images/payment-methods/ach-eft.svg';
        break;
      case PaymentMethod.TypeEnum.EFT_DEBIT:
        temp.transaction_type = 'assets/images/payment-methods/ach-eft.svg';
        break;
    }

    return temp;
  }

  getViewModelsFromIndependentCredits(independentCredits: IndependentCredit[]): IndependentCreditViewModel[] {
    const viewModels: IndependentCreditViewModel[] = [];
    if (!independentCredits || independentCredits.length === 0) {
      const temp: IndependentCreditViewModel = new IndependentCreditViewModel();
      viewModels.push(temp);
      return viewModels;
    }
    for (const ic of independentCredits) {
      let viewModel = this.convertIndependentCreditToViewModel(ic, this.isMerchant);

      viewModels.push(viewModel);
    }
    return viewModels;
  }

  rowClickedCallback = (data: IndependentCreditViewModel, event?: MouseEvent): void => {
    const url = `${data.merchant_id}/${INDEPENDENT_CREDITS_ROUTE}/${data.id}`;

    // Open a new window (Shift) or tab (Ctrl/Option)
    if (event?.shiftKey || event?.ctrlKey || event?.metaKey) {
      window.open(url, event?.shiftKey ? '_blank' : undefined);
    } else {
      this._router.navigate([url]);
    }
  };

  updateTable(): void {
    this.sortColumnKeys = this.customColumns
      .filter((c) => c.active && !c.hidden)
      .map((c) => c.columnKey as keyof IndependentCreditViewModel);
    this.hideColumnKeys = this.customColumns
      .filter((c) => !c.active || c.hidden)
      .map((c) => c.columnKey as keyof IndependentCreditViewModel);
    localStorage.setItem(CURRENT_INDEPENDENT_CREDITS_CUSTOM_COLUMN_KEY, JSON.stringify(this.customColumns));
  }

  buildInputColumns(): void {
    try {
      const existingColumns = JSON.parse(localStorage.getItem(CURRENT_INDEPENDENT_CREDITS_CUSTOM_COLUMN_KEY));
      if (existingColumns) {
        this.customColumns = existingColumns;
      }
    } catch (err) {
      //just start fresh if local storage is corrupted
    }

    const defaultCustomColumns = [
      {
        columnKey: 'created_at',
        columnDescription: 'Transaction date',
        editable: true,
        hidden: false,
        active: true,
      },
      {
        columnKey: 'merchant_name',
        columnDescription: 'Merchant',
        editable: true,
        hidden: false,
        active: true,
      },
      {
        columnKey: 'transaction_type',
        columnDescription: 'Payment method',
        editable: true,
        hidden: false,
        active: true,
      },
      {
        columnKey: 'last_4',
        columnDescription: 'Number',
        editable: true,
        hidden: false,
        active: true,
      },
      {
        columnKey: 'currency',
        columnDescription: 'Currency',
        editable: true,
        hidden: false,
        active: false,
      },
      {
        columnKey: 'status',
        columnDescription: 'Status',
        editable: true,
        hidden: false,
        active: true,
      },
      {
        columnKey: 'external_payment_id',
        columnDescription: 'External payment Id',
        editable: true,
        hidden: false,
        active: true,
      },
      {
        columnKey: 'amount',
        columnDescription: 'Amount',
        editable: true,
        hidden: false,
        active: true,
      },
      {
        columnKey: 'independent_credit_id',
        columnDescription: 'Independent credit Id',
        editable: true,
        hidden: false,
        active: false,
      },
    ];
    // want to ensure any new columns are included
    // note: right now this function does not remove deprecated columns
    const newColumns = defaultCustomColumns.filter(
      (dCC) => !this.customColumns?.find((cC) => cC.columnKey === dCC.columnKey),
    );

    for (const customColumn of this.customColumns) {
      const defaultColumn = defaultCustomColumns.find((dCC) => dCC.columnKey === customColumn.columnKey);
      if (defaultColumn) {
        customColumn.columnDescription = defaultColumn.columnDescription;
      }
    }

    this.customColumns.push(...newColumns);
    if (this.isMerchant) {
      this.customColumns.find((cC) => cC.columnKey === 'merchant_name').hidden = true;
      this.customColumns.find((cC) => cC.columnKey === 'currency').hidden = true;
    } else {
      this.customColumns.find((cC) => cC.columnKey === 'merchant_name').hidden = false;
      this.customColumns.find((cC) => cC.columnKey === 'currency').hidden = false;
    }

    this.updateTable();
  }
}

type IndependentCreditsListViewModel = keyof IndependentCreditViewModel;

export class IndependentCreditViewModel {
  independentCredit: IndependentCredit;

  id: string;

  merchant_id: string;

  @Column({
    order: 0,
    name: 'Transaction Date',
    styling: 'min-width:210px',
    dateTooltip: true,
  })
  created_at: string;

  // displayed, but also needed to display currency of amount
  @Column({
    order: 1,
    name: 'Currency',
    styling: 'min-width:70px;',
  })
  currency: string;

  @Column({
    order: 2,
    name: 'Status',
    isChip: true,
    chipConfig: 'status_chip_config',
    styling: 'min-width:140px;',
  })
  status: string;
  status_chip_config: TilledChipConfig;

  @Column({
    order: 3,
    name: 'Amount',
    isCurrency: true,
    styling: 'min-width:100px;',
  })
  amount: number;

  @Column({
    order: 4,
    name: 'Merchant',
    styling: 'padding-right: 2ch; width: 20ch; max-width: 20ch;',
  })
  merchant_name: string;

  @Column({
    order: 5,
    name: 'Payment Method',
    styling: 'min-width:80px;',
    imageStyling: 'max-width: 40px; min-width: 40px; max-height: 40px; min-height: 40px',
    isImage: true,
  })
  transaction_type: string;

  @Column({
    order: 6,
    name: 'Number',
    styling: 'min-width:80px;',
    headerTooltip:
      'The number reflects the last 4 digits of a card number or the last 2 digits of a bank account number.',
  })
  last_4: string;

  @Column({
    order: 7,
    name: 'External Payment Id',
    styling: 'min-width:225px',
    headerTooltip: 'The unique identifier for the payment from the external gateway.',
  })
  external_payment_id: string;

  @Column({
    order: 8,
    name: 'Independent Credit Id',
    styling: 'min-width:225px;',
  })
  independent_credit_id: string;

  @Column({
    order: 9,
    isActionList: true,
  })
  action: ActionListItem[];
}
